<template>
  <AppPlaceSearchResults
    v-if="place && seo"
    :place="place"
    :seo="seo"
    :pageSize="30"
    :searchParameters="sp.searchParameters"
  />

  <Container class="my-8" v-if="place">
    <SearchCommonSearch :place="place" :basePath="basePath" :keywords-list="keywords ?? []" :seo="seo" />
  </Container>

  <PlaceFaqGrid v-if="faqs?.length" :faqs="faqs" />
  <PlaceNearbyPlaceGrid v-if="place && nearbyPlaces?.length" :place="place" :nearby-places="nearbyPlaces" />
</template>

<script setup lang="ts">
import { getCachedGooglePlaceByName } from '~/lib/google-maps/google-place'
import { AppProviderKeys } from '~/providers'
import type { LanguageCodes, Place } from '~/types/api'
import type { IPSearchParameters } from '~/types/provider/search-parameters'
import type { ISearchFilters } from '~/types/search/filters'
import { captureError } from '~/lib/logger'

const props = defineProps<{
  place: Place
  titleOverride?: string
}>()

const { locale } = useI18n()
const { getKeywords, getKeywordSearchParams } = useCommonSearch()
const { getRoutePlaceInfo } = usePlaceTransformation()
const { GetGeocoderService } = useGooglePlaceLib()
const { getMarketplaceApiClient, getPlacesApiClient } = useApiClients()

const sp = inject<IPSearchParameters>(AppProviderKeys.SearchParameters) as IPSearchParameters

const routePlaceInfo = computed(() => getRoutePlaceInfo())
const basePath = computed(() => routePlaceInfo.value.basePath)
const keyword = computed(() => routePlaceInfo.value.keyword)
const keywordKey = ref(`keywordPage-${locale.value}`)

// Marketplace api requests
const { data: seo, error: seoError } = await useAsyncData(`place-seo-${props.place.id}`, async () => {
  const { data: seoResponse } = await getMarketplaceApiClient().GET('/Place/Seo/{placeId}', {
    params: {
      path: {
        placeId: props.place.id as string
      },
      query: {
        languageCode: locale.value as LanguageCodes,
        keyword: keyword?.value ?? undefined
      }
    }
  })

  return seoResponse ?? {}
})

if (seoError.value) captureError(toRaw(seoError.value) ?? new Error('Error fetching data for SEO!'))

const seoTitle = computed(() => (seo.value?.title ? seo.value.title + ' |' : ''))

useHead({
  title: `${seoTitle.value} RVezy Marketplace`,
  meta: [{ name: 'description', content: seo.value?.description }]
})

const { data: faqs, error: faqError } = await useAsyncData(`place-faqs-${props.place.id}`, async () => {
  const { data: faqsResponse } = await getMarketplaceApiClient().GET('/Place/Faqs/{placeId}', {
    params: {
      path: {
        placeId: props.place.id as string
      },
      query: {
        languageCode: locale.value as LanguageCodes
      }
    }
  })

  return faqsResponse?.results ?? []
})

if (faqError.value) captureError(toRaw(faqError.value) ?? new Error('Error fetching data for Faq!'))

const { data: keywords, error: keywordsError } = await getKeywords(keywordKey, (locale.value as LanguageCodes) ?? 'En')
if (keywordsError.value) captureError(toRaw(keywordsError.value) ?? new Error('Error fetching data for keywords!'))

const { data: nearbyPlaces, error: nearbyPlacesError } = await useAsyncData(
  `nearby-places-${props.place.id}`,
  async () => {
    const { data: nearbyPlacesResponse } = await getPlacesApiClient().GET('/Search', {
      params: {
        query: {
          PlaceId: props.place.id,
          Latitude: props.place.latitude,
          Longitude: props.place.longitude,
          PlaceType: props.place.placeType === 'Country' ? 'Region' : 'City',
          PageSize: props.place.placeType === 'Country' ? 100 : 20,
          DistanceKm: 10000,
          LanguageCode: locale.value as LanguageCodes
        }
      }
    })

    return nearbyPlacesResponse?.results as Place[]
  }
)

if (nearbyPlacesError.value)
  captureError(toRaw(nearbyPlacesError.value) ?? new Error('Error fetching data for NearbyPlaces!'))

const validKeyword = !keyword.value || keywords.value?.map((k) => k.slug).includes(keyword.value ?? '')

//Nav to the home page is the pace is null or the keyword exists and is not in the list of keywords
if (!props.place || !validKeyword) {
  await localeNavigateTo({ name: 'index' }, { redirectCode: 301 })
}

if (keyword.value) {
  const filter = getKeywordSearchParams(keywords.value, keyword.value)
  if (filter.filterKey && filter.filterValue) {
    sp.updateFilters({} as ISearchFilters) // Clear all filters to prevent multiple keyword filters at the same time
    sp.updateFilter(filter.filterKey, filter.filterValue, true)
  }
}

if (props.place?.fullPlaceName) {
  // Place with already known location, don't need autocomplete, so it gets from cache, saving some $$$
  const geocoderService = await GetGeocoderService()
  const googleLocation = await getCachedGooglePlaceByName(props.place.fullPlaceName, geocoderService)
  if (googleLocation) sp.updateLocation(googleLocation)
}
</script>
